import type { TFunction } from 'i18next'
import { ZodError } from 'zod'
import { authClient } from '@/lib/auth-client'

/** Errore dell'API: `code` è il messaggio del backend (`{ error: 'emailInUse' }`), tradotto in `admin.errors.<code>`. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    /** Body JSON della risposta: la schedina ci legge le quote nuove del 409 `oddsChanged`. */
    readonly body: unknown = null,
  ) {
    super(code)
  }
}

type ApiInit = Omit<RequestInit, 'body'> & { json?: unknown; guestToken?: string }

/** Chiamata same-origin all'API (cookie di sessione inclusi); JSON in entrata e in uscita. `guestToken` autentica un ospite (X-Guest-Token) invece della sessione admin. */
export async function apiFetch<T>(path: string, { json, headers, guestToken, ...init }: ApiInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(json === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(guestToken === undefined ? {} : { 'X-Guest-Token': guestToken }),
      ...headers,
    },
    body: json === undefined ? undefined : JSON.stringify(json),
  })
  // Sessione scaduta o utente disabilitato: riletta la sessione, ProtectedRoute rimanda al login.
  // Un 401 con guestToken riguarda l'ospite, non l'admin: non deve far scattare il redirect al login.
  if (response.status === 401 && guestToken === undefined) authClient.$store.notify('$sessionSignal')
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null
    throw new ApiError(response.status, body?.error ?? 'generic', body)
  }
  return (response.status === 204 ? undefined : await response.json()) as T
}

/** Messaggio leggibile per un errore di `apiFetch` (o di rete), o per una `ZodError` di validazione lato client
 * (es. `TournamentSettingsSchema.parse` in `tournament-form.ts`): senza questo caso finirebbe tra gli errori
 * di rete, fuorviante per un problema nei valori inseriti nel form. */
export function errorMessage(t: TFunction, error: unknown): string {
  if (error instanceof ZodError) return t('admin.zambepong.invalidSettings')
  const code = error instanceof ApiError ? error.code : 'network'
  return t(`admin.errors.${code}`, { defaultValue: t('admin.errors.generic') })
}
